import { useCallback, useMemo, useRef, useState } from 'react'
import { useSeoMeta } from '@unhead/react'
import type { Category } from '@/lib/calculators'
import { CALCULATORS } from '@/lib/calculators'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import CategoryRail from '@/components/CategoryRail'
import CircleCalculator from '@/components/calculators/CircleCalculator'
import FractionCalculator from '@/components/calculators/FractionCalculator'
import TrapezoidCalculator from '@/components/calculators/TrapezoidCalculator'
import InterestCalculator from '@/components/calculators/InterestCalculator'
import RoundingCalculator from '@/components/calculators/RoundingCalculator'
import {Sparkles} from 'lucide-react'

function Home() {
  useSeoMeta({
    title: 'Math Calculator - Free Interactive Solvers with Step-by-Step Visual Explanations',
    description: 'Free math calculator with interactive solvers: circle circumference, mixed fractions, trapezoid area, simple interest, rounding. Visual SVG models & step-by-step solutions for students and teachers.',
    ogTitle: 'Math Calculator - Interactive Visual Solvers | FunMathCalculator',
    ogDescription: 'Free interactive math calculators with step-by-step explanations and live SVG visualizations.',
    ogImage: 'https://www.funmathcalculator.com/og-image.jpg',
    ogUrl: 'https://www.funmathcalculator.com/',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Math Calculator - Interactive Visual Solvers',
    twitterDescription: 'Free interactive math calculators with step-by-step explanations.',
    twitterImage: 'https://www.funmathcalculator.com/og-image.jpg'
  })

  const [active, setActive] = useState<Category>('all')
  const [highlight, setHighlight] = useState<string>('')

  const refs = {
    circle: useRef<HTMLElement>(null),
    fraction: useRef<HTMLElement>(null),
    trapezoid: useRef<HTMLElement>(null),
    interest: useRef<HTMLElement>(null),
    rounding: useRef<HTMLElement>(null),
  }

  const handleJump = useCallback((id: string) => {
    const r = (refs as any)[id]?.current as HTMLElement | undefined
    if (!r) return
    r.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setHighlight(id)
    setTimeout(() => setHighlight(''), 1800)
  }, [refs])

  const visible = useMemo(() => {
    if (active === 'all') return CALCULATORS.map(c => c.id)
    return CALCULATORS.filter(c => c.category === active).map(c => c.id)
  }, [active])

  const isVisible = (id: string) => visible.includes(id)

  return (
    <div id="top" className="min-h-screen text-slate-100">
      <Header onJump={handleJump} />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-14 pb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-400/20 bg-emerald-400/[0.05] text-[11px] uppercase tracking-[0.18em] text-emerald-300 mb-6">
          <Sparkles className="w-3 h-3" /> 5 Interactive Solvers · 117K+ Monthly Searches
        </div>
        <h1 className="font-display text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight max-w-4xl">
          Math, made <span className="text-emerald-400">visual</span>.<br className="hidden md:block" />
          Solve, drag, and watch the formulas come to life.
        </h1>
        <p className="mt-5 text-slate-400 text-base md:text-lg max-w-2xl leading-relaxed">
          A focused playground for the math you actually look up — circles, fractions, trapezoids, simple interest, and rounding.
          Drag a slider, see the shape morph, and copy a clean step-by-step explanation in one click.
        </p>

        <div className="mt-8">
          <CategoryRail active={active} onChange={setActive} />
        </div>
      </section>

      {/* Solvers */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 pb-10 space-y-8">
        {isVisible('circle') && <CircleCalculator ref={refs.circle} highlighted={highlight === 'circle'} />}
        {isVisible('fraction') && <FractionCalculator ref={refs.fraction} highlighted={highlight === 'fraction'} />}
        {isVisible('trapezoid') && <TrapezoidCalculator ref={refs.trapezoid} highlighted={highlight === 'trapezoid'} />}
        {isVisible('interest') && <InterestCalculator ref={refs.interest} highlighted={highlight === 'interest'} />}
        {isVisible('rounding') && <RoundingCalculator ref={refs.rounding} highlighted={highlight === 'rounding'} />}

        {visible.length === 0 && (
          <div className="glass rounded-2xl p-12 text-center text-slate-400">No solvers in this category yet — try "All Solvers".</div>
        )}
      </main>

      <Footer />
    </div>
  )
}

export default Home
